import React from 'react';

export default function LegalModal({ type, onClose, lang = 'pl' }) {
  if (!type) return null;

  const isTerms = type === 'terms';
  const isPl = lang === 'pl';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fadeIn">
      <div 
        className="bg-slate-900 border border-slate-800 rounded-[28px] max-w-2xl w-full p-6 sm:p-8 max-h-[85vh] overflow-y-auto shadow-2xl relative text-slate-300 text-sm leading-relaxed"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 pb-4 mb-6">
          <div className="flex items-center gap-2.5">
            <span className="material-symbols-outlined text-emerald-400 text-2xl">
              {isTerms ? 'gavel' : 'shield_person'}
            </span>
            <h2 className="text-xl font-black text-white tracking-tight">
              {isTerms 
                ? (isPl ? 'Regulamin Serwisu (Terms & Conditions)' : 'Terms & Conditions')
                : (isPl ? 'Polityka Prywatności (Privacy Policy)' : 'Privacy Policy')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition cursor-pointer"
            title={isPl ? 'Zamknij' : 'Close'}
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        {/* Document Body */}
        {isTerms ? (
          <div className="space-y-4 text-xs sm:text-sm">
            <p className="text-slate-400 italic">
              {isPl 
                ? 'Ostatnia aktualizacja: 9 sierpnia 2026 r.' 
                : 'Last Updated: August 9, 2026'}
            </p>

            <section className="space-y-2">
              <h3 className="text-sm font-extrabold text-emerald-400 uppercase tracking-wider">
                1. {isPl ? 'Akceptacja Warunków' : 'Acceptance of Terms'}
              </h3>
              <p>
                {isPl
                  ? 'Tworząc konto lub korzystając z platformy GymGhost, akceptujesz niniejszy Regulamin oraz zobowiązujesz się do jego przestrzegania. Jeśli nie zgadzasz się z którykolwiek z postanowień, prosimy o zaprzestanie korzystania z serwisu.'
                  : 'By creating an account or using the GymGhost platform, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you must refrain from using the service.'}
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-extrabold text-emerald-400 uppercase tracking-wider">
                2. {isPl ? 'Opis Usługi' : 'Description of Service'}
              </h3>
              <p>
                {isPl
                  ? 'GymGhost to aplikacja internetowa i mobilna przeznaczona do rejestracji treningów siłowych, analityki progresywnego przeładowania, obliczania ciężarów (plate calculator), 1RM oraz współpracy zawodnika z trenerem osobistym.'
                  : 'GymGhost is a web and mobile application designed for logging strength workouts, progressive overload tracking, 1RM analytics, plate math calculations, and coach-athlete collaboration.'}
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-extrabold text-emerald-400 uppercase tracking-wider">
                3. {isPl ? 'Konta Użytkowników' : 'User Accounts'}
              </h3>
              <p>
                {isPl
                  ? 'Użytkownik jest odpowiedzialny za zachowanie poufności danych logowania oraz za wszelkie aktywności podejmowane na jego koncie. GymGhost zastrzega sobie prawo do zablokowania kont naruszających zasady bezpieczeństwa.'
                  : 'Users are responsible for maintaining the confidentiality of their account login credentials and for all activities that occur under their account. GymGhost reserves the right to suspend accounts violating safety policies.'}
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-extrabold text-emerald-400 uppercase tracking-wider">
                4. {isPl ? 'Własność Intelektualna' : 'Intellectual Property'}
              </h3>
              <p>
                {isPl
                  ? 'Wszelkie prawa do kodu źródłowego, znaku towarowego GymGhost, algorytmów analitycznych i interfejsu graficznego należą do GymGhost.'
                  : 'All rights to the source code, GymGhost trademark, analytics algorithms, and visual interfaces belong exclusively to GymGhost.'}
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-extrabold text-emerald-400 uppercase tracking-wider">
                5. {isPl ? 'Wyłączenie Odpowiedzialności Medycznej' : 'Health & Exercise Disclaimer'}
              </h3>
              <p>
                {isPl
                  ? 'GymGhost nie świadczy porad medycznych ani trenerskich. Przed rozpoczęciem jakiegokolwiek programu treningu siłowego zaleca się konsultację z lekarzem lub wykwalifikowanym trenerem.'
                  : 'GymGhost does not provide medical or formal health advice. Before initiating any heavy weightlifting or strength training regimen, consult a physician or certified fitness professional.'}
              </p>
            </section>

            <section className="space-y-2 pt-2 border-t border-slate-800">
              <p className="text-slate-400 text-xs">
                {isPl ? 'Kontakt w sprawach regulaminu: ' : 'Questions regarding terms: '}
                <a href="mailto:contact@gymghost.net" className="text-emerald-400 underline font-bold">contact@gymghost.net</a>
              </p>
            </section>
          </div>
        ) : (
          <div className="space-y-4 text-xs sm:text-sm">
            <p className="text-slate-400 italic">
              {isPl 
                ? 'Ostatnia aktualizacja: 9 sierpnia 2026 r.' 
                : 'Last Updated: August 9, 2026'}
            </p>

            <section className="space-y-2">
              <h3 className="text-sm font-extrabold text-emerald-400 uppercase tracking-wider">
                1. {isPl ? 'Gromadzone Dane' : 'Information We Collect'}
              </h3>
              <p>
                {isPl
                  ? 'Gromadzimy wyłącznie dane niezbędne do świadczenia usługi: adres e-mail, nazwy użytkownika, zapisane wpisy dziennika treningowego (ćwiczenia, serie, powtórzenia, ciężary, wskaźniki RPE) oraz przypisania trenera/zawodnika.'
                  : 'We collect only the minimum data required to deliver our platform services: email address, profile names, workout logs (exercises, sets, reps, weight, RPE ratings), and athlete-coach linkages.'}
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-extrabold text-emerald-400 uppercase tracking-wider">
                2. {isPl ? 'Wykorzystanie Danych' : 'How We Use Your Data'}
              </h3>
              <p>
                {isPl
                  ? 'Twoje dane treningowe są wykorzystywane do wyliczania statystyk siłowych, progresywnego przeładowania oraz (w przypadku kont połączonych) udostępniania wyników Twojemu osobistemu trenerowi.'
                  : 'Your workout data is used solely to generate strength analytics, progressive overload targets, and (if connected) share training progress with your personal coach.'}
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-extrabold text-emerald-400 uppercase tracking-wider">
                3. {isPl ? 'Bezpieczeństwo i Architektura' : 'Data Security & Storage'}
              </h3>
              <p>
                {isPl
                  ? 'Dane są przechowywane i szyfrowane w infrastrukturze Google Firebase (Cloud Firestore & Authentication). Stosujemy rygorystyczne reguły bezpieczeństwa uniemożliwiające dostęp nieuprawnionym stronom.'
                  : 'All records are encrypted and stored within Google Firebase Cloud Infrastructure (Cloud Firestore & Authentication). Strict security rules strictly isolate user data.'}
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-extrabold text-emerald-400 uppercase tracking-wider">
                4. {isPl ? 'Prawa Użytkownika (RODO / GDPR)' : 'Your Rights (GDPR)'}
              </h3>
              <p>
                {isPl
                  ? 'Masz prawo do wglądu w swoje dane, ich sprostowania, eksportu oraz pełnego, trwałego usunięcia konta wraz ze wszystkimi wpisami treningowymi w dowolnym momencie.'
                  : 'You have full rights to access, export, update, or permanently delete your account and all associated workout history at any time.'}
              </p>
            </section>

            <section className="space-y-2 pt-2 border-t border-slate-800">
              <p className="text-slate-400 text-xs">
                {isPl ? 'Kontakt w sprawach ochrony prywatności: ' : 'Privacy inquiries: '}
                <a href="mailto:contact@gymghost.net" className="text-emerald-400 underline font-bold">contact@gymghost.net</a>
              </p>
            </section>
          </div>
        )}

        {/* Modal Action Footer */}
        <div className="mt-8 pt-4 border-t border-slate-800 flex justify-end">
          <button
            onClick={onClose}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold px-6 py-2.5 rounded-xl text-xs transition cursor-pointer shadow-md"
          >
            {isPl ? 'Zamknij' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}
